import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody } from 'reactstrap';
import PropTypes from 'prop-types';
import withLoader from 'components/organisms/withLoader';
import * as Yup from 'yup';
import {
  API_MEDICATION,
  API_UPDATE_MEDICATION,
} from 'services/dataService/ccmResources';
import Pagination from 'components/molecules/paginationTransition';
import { showResult } from 'utils';
import useTable from 'utils/useTable';
import useValidation from 'utils/useValidation';
import { useParams } from 'react-router-dom';
import common from 'constants/common';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import forms from 'constants/forms';
import TableActions from '../components/tableActions';
import EditableTableRow from './components/editableTableRow';
import NewMedicationRow from './components/newRow';

const VALIDATION_RULES = Yup.object().shape(
  {
    name: Yup.string().required('required').typeError('required'),
    startDate: Yup.date()
      .when(['endDate'], {
        is: (endDate) => endDate,
        then: Yup.date()
          .max(Yup.ref('endDate'), `Start date must be earlier than End date`)
          .nullable(),
      })
      .nullable(),
    endDate: Yup.date()
      .when(['startDate'], {
        is: (startDate) => startDate,
        then: Yup.date()
          .min(Yup.ref('startDate'), `End date must be later than Start date`)
          .nullable(),
      })
      .nullable(),
  },
  [['endDate', 'startDate']],
);
const validateAndSaveChanges =
  (validateRow, saveData, reloader) => async () => {
    const result = await validateRow();
    if (result.isValid) {
      const { error } = await saveData();
      if (error) showResult(error);
      else showResult();
      reloader();
    }
  };
const PER_PAGE = 10;
const createRow = () => ({
  name: '',
  duration: '',
  dosage: '',
  route: '',
  frequency: '',
  note: '',
  startDate: null,
  endDate: null,
});

const Medication = ({ canEditPage }) => {
  const {
    ccmPatientDetails: { setMedications },
  } = useDispatch();

  const { patientGuid } = useParams();
  const [isRowSaved, setIsRowSaved] = useState(true);
  const [isEditing, setEditing] = useState(false);
  const [editingRowNumber, setEditingRowNumber] = useState(-1);
  const { row, setRow, validate, messageErrors } = useValidation(
    {},
    VALIDATION_RULES,
  );
  const {
    data: { items },
    count,
    loading,
    page,
    filters,
    setPage,
    setFilters,
    saveNewRow,
    updateRow,
    deleteRow,
  } = useTable(
    API_MEDICATION,
    PER_PAGE,
    { patientGuid },
    {
      makeInitialRequest: false,
    },
  );

  useEffect(() => {
    setMedications(items);
  }, [items]);

  const createNewRow = () => {
    setIsRowSaved(false);
    setRow(createRow());
  };

  const clearRows = () => {
    setIsRowSaved(true);
    setEditing(false);
    setEditingRowNumber(-1);
    setRow({});
  };

  const deleteTableRow = async (guid) => {
    const { error } = await deleteRow(API_MEDICATION, guid);
    showResult(error, common.submitSuccessTypes.changed);
  };

  useEffect(() => {
    setFilters({ patientGuid });
    clearRows();
  }, [patientGuid]);

  const filterValue = useMemo(() => {
    const allValues = forms.medicationsFilterSelect;
    return allValues.find((e) => e.value === filters.isActive) || allValues[0];
  }, [filters.isActive]);

  return (
    <Card className="card-border ccm-table-section">
      <CardBody>
        {withLoader(
          loading,
          <div className="table-custom-scrollbar">
            <div className="table-outer medication-editable-table">
              <h3 className="ccm-table-section__h3">Medication</h3>
              <p>Show Status</p>
              <div className="table_filter">
                <Select
                  options={forms.medicationsFilterSelect}
                  defaultValue={forms.medicationsFilterSelect[0]}
                  classNamePrefix="select-type"
                  value={filterValue}
                  onChange={({ value }) => {
                    setFilters({
                      patientGuid,
                      isActive: value || undefined,
                    });
                  }}
                />
              </div>
              <table cellSpacing="0" cellPadding="0" className="table-border">
                <thead>
                  <tr>
                    <th>Medication</th>
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Duration</th>
                    <th>Dose</th>
                    <th>Route</th>
                    <th>Frequency</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {!!items.length &&
                    items.map((el, index) => (
                      <EditableTableRow
                        key={index}
                        rowIndex={index}
                        editing={setEditing}
                        isRowEditing={isEditing}
                        editingRowNumber={editingRowNumber}
                        changeRowNumber={setEditingRowNumber}
                        medication={{
                          guid: el.guid,
                          isActive: el.isActive,
                          name: el.name,
                          startDate: el.startDate,
                          endDate: el.endDate,
                          duration: el.duration,
                          dosage: el.dosage,
                          route: el.route,
                          frequency: el.frequency,
                          note: el.note,
                        }}
                        messageErrors={messageErrors}
                        author={{
                          ...el.author,
                          date: el.updatedAt,
                        }}
                        onEdit={setRow}
                        onDelete={() => deleteTableRow(el.guid)}
                        canEditPage={canEditPage}
                      />
                    ))}
                  {!isRowSaved && !isEditing && (
                    <NewMedicationRow
                      messageErrors={messageErrors}
                      initialRow={createRow()}
                      newRow={row}
                      setRow={setRow}
                    />
                  )}
                </tbody>
              </table>
            </div>
            <div className="ccm-table-section__table-action--sticky">
              <TableActions
                save={validateAndSaveChanges(
                  validate,
                  () =>
                    !isEditing
                      ? saveNewRow(API_MEDICATION, {
                          ...row,
                          patientGuid,
                        })
                      : updateRow(API_UPDATE_MEDICATION(patientGuid), {
                          ...row,
                          patientGuid,
                        }),
                  clearRows,
                )}
                cancel={clearRows}
                new={createNewRow}
                rowIsSaved={isRowSaved}
                isEditing={isEditing}
                isAdminPanel={canEditPage}
              />
            </div>

            <div className="ccm-table-section__pagination ccm-table-section__table-action--sticky">
              <Pagination
                title=""
                page={page}
                pageTotal={items.length}
                itemsPerPage={PER_PAGE}
                total={count}
                handleFirstClick={() => setPage(1)}
                handleLastClick={() => setPage(Math.ceil(count / PER_PAGE))}
                handlePrevClick={() => setPage(page - 1)}
                handleNextClick={() => setPage(page + 1)}
                handleGoToPage={(numberPage) => setPage(numberPage)}
              />
            </div>
          </div>,
        )}
      </CardBody>
    </Card>
  );
};

Medication.propTypes = {
  canEditPage: PropTypes.bool,
};

export default Medication;
